/**
 * The ElevenLabs voice (RFC 0307 S5): a second adapter behind `VoiceProvider`.
 *
 * The same seam as OpenAI Realtime, which is the point of the slice: the
 * session plan (tools, transcript, notices) is unchanged, and only this file
 * knows the provider. The secret is a signed URL the proxy minted for the
 * configured ElevenLabs agent; the instructions ride as a prompt override.
 * The agent must declare the same four client tools.
 */

import { Conversation, type MessagePayload } from '@elevenlabs/client'
import { openLine } from './browserCall'
import { callLanguage } from './language'
import type { VoiceCall, VoiceConnectOptions, VoiceProvider } from './types'

/** Read who said a message: the person, or the agent speaking as the voice. */
export function elevenlabsSpeaker(m: MessagePayload): 'person' | 'voice' {
  const who = String((m as { role?: unknown }).role ?? m.source)
  return who === 'user' ? 'person' : 'voice'
}

// ElevenLabs contextual updates are silent. A user message is the SDK's way
// to ask the agent to take a turn; keep that app-generated prompt off the
// person's call transcript.
export const NOTICE_REPLY_PROMPT =
  'Please tell me the latest work update now, including any question I need to answer.'

export function deliverElevenLabsNotice(
  conversation: Pick<Conversation, 'sendContextualUpdate' | 'sendUserMessage'>,
  text: string,
  speak: boolean,
): void {
  conversation.sendContextualUpdate(text)
  if (speak) conversation.sendUserMessage(NOTICE_REPLY_PROMPT)
}

export function elevenlabsTranscript(
  message: MessagePayload,
): { speaker: 'person' | 'voice'; text: string } | null {
  const text = String(message.message ?? '').trim()
  if (!text) return null
  const speaker = elevenlabsSpeaker(message)
  if (speaker === 'person' && text === NOTICE_REPLY_PROMPT) return null
  return { speaker, text }
}

export function elevenlabsProvider(): VoiceProvider {
  return {
    async connect(opts: VoiceConnectOptions): Promise<VoiceCall> {
      const line = openLine(opts)
      const clientTools = Object.fromEntries(
        opts.tools.map((t) => [
          t.name,
          async (params: unknown) => {
            try {
              return JSON.stringify(await opts.onToolCall(t.name, params ?? {}))
            } catch (err) {
              return JSON.stringify({
                error: err instanceof Error ? err.message : String(err),
              })
            }
          },
        ]),
      )
      const conversation = await Conversation.startSession({
        signedUrl: opts.clientSecret,
        connectionType: 'websocket',
        overrides: {
          agent: {
            prompt: { prompt: opts.instructions },
            language: callLanguage() as never,
          },
        },
        clientTools,
        onMessage: (m: MessagePayload) => {
          const transcript = elevenlabsTranscript(m)
          if (transcript) opts.onTranscript(transcript)
        },
        onDisconnect: (details) => {
          line.end(
            details.reason === 'error'
              ? { error: details.message || 'the voice call dropped' }
              : {},
          )
        },
        onError: (message: string) => {
          console.warn('[voice] elevenlabs error:', message)
        },
      })
      line.onTeardown(() => void conversation.endSession())
      // Hung up while connecting: the session is ended above, and the line is
      // not claimed, which would end the call that replaced it.
      line.throwIfHungUp()
      line.claim()
      line.level.start(() => conversation.getInputVolume() * 2)
      line.live()

      return {
        notify(text, options) {
          deliverElevenLabsNotice(conversation, text, options?.speak === true)
        },
        setMicMuted(muted) {
          line.level.setMuted(muted)
          conversation.setMicMuted(muted)
        },
        setSpeakerMuted(muted) {
          conversation.setVolume({ volume: muted ? 0 : 1 })
        },
        onLevel: line.level.onLevel,
        hangUp() {
          line.end()
        },
      }
    },
  }
}
